import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { DraggableProvinceBlock } from './DraggableProvinceBlock';
import { useGameStore } from '@/store/gameStore';
import { ProvinceBlock } from '@/types';
import clsx from 'clsx';

interface ProvinceBlocksContainerProps {
  showProvinceNames?: boolean;
  showAbbreviations?: boolean;     // 添加简称拼音显示控制
  hintedProvinceId?: string | null;
}

export const ProvinceBlocksContainer: React.FC<ProvinceBlocksContainerProps> = ({
  showProvinceNames = false,
  showAbbreviations = true,       // 默认显示简称拼音
  hintedProvinceId = null
}) => {
  const { provinces, completedProvinces, isGameStarted, showAbbreviations: globalShowAbbreviations } = useGameStore();
  const [draggedProvince, setDraggedProvince] = useState<ProvinceBlock | null>(null);

  // 按区域分组省份
  const groupProvincesByRegion = () => {
    const regions = {
      north: { name: '华北地区', provinces: [] as ProvinceBlock[] },
      northeast: { name: '东北地区', provinces: [] as ProvinceBlock[] },
      east: { name: '华东地区', provinces: [] as ProvinceBlock[] },
      south: { name: '华南地区', provinces: [] as ProvinceBlock[] },
      central: { name: '华中地区', provinces: [] as ProvinceBlock[] },
      southwest: { name: '西南地区', provinces: [] as ProvinceBlock[] },
      northwest: { name: '西北地区', provinces: [] as ProvinceBlock[] },
      special: { name: '特别行政区', provinces: [] as ProvinceBlock[] }
    };

    provinces.forEach(province => {
      switch (province.id) {
        case 'beijing':
        case 'tianjin':
        case 'hebei':
        case 'shanxi':
        case 'neimenggu':
          regions.north.provinces.push(province);
          break;
        case 'liaoning':
        case 'jilin':
        case 'heilongjiang':
          regions.northeast.provinces.push(province);
          break;
        case 'shanghai':
        case 'jiangsu':
        case 'zhejiang':
        case 'anhui':
        case 'fujian':
        case 'jiangxi':
        case 'shandong':
          regions.east.provinces.push(province);
          break;
        case 'guangdong':
        case 'guangxi':
        case 'hainan':
          regions.south.provinces.push(province);
          break;
        case 'henan':
        case 'hubei':
        case 'hunan':
          regions.central.provinces.push(province);
          break;
        case 'chongqing':
        case 'sichuan':
        case 'guizhou':
        case 'yunnan':
        case 'tibet':
          regions.southwest.provinces.push(province);
          break;
        case 'shaanxi':
        case 'gansu':
        case 'qinghai':
        case 'ningxia':
        case 'xinjiang':
          regions.northwest.provinces.push(province);
          break;
        default:
          regions.special.provinces.push(province);
      }
    });

    return regions;
  };

  const handleDragStart = (province: ProvinceBlock) => {
    setDraggedProvince(province);
  };

  const handleDragEnd = () => {
    setDraggedProvince(null);
  };

  if (!isGameStarted) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="text-4xl mb-3">🧩</div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            拼图块区域
          </h3>
          <p className="text-gray-500 text-sm">
            开始游戏后，拼图块将出现在这里
          </p>
        </div>
      </div>
    );
  }

  const regionGroups = groupProvincesByRegion();
  const uncompletedProvinces = provinces.filter(p => !completedProvinces.includes(p.id));
  const completedCount = completedProvinces.length;
  const totalCount = provinces.length;

  return (
    <div className="w-full bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden 
      min-h-[900px] lg:min-h-[1100px] xl:min-h-[1300px] 2xl:min-h-[1500px] 
      flex flex-col">
      {/* 头部统计 - 适配10%宽度 */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-2 border-b border-gray-200 flex-shrink-0">
        <div className="text-center">
          <h3 className="text-sm font-semibold text-gray-800">拼图块</h3>
          <div className="text-lg font-bold text-blue-600">
            {totalCount - completedCount}
          </div>
          <div className="text-xs text-gray-500">剩余</div>
        </div>
        
        {/* 进度条 */}
        <div className="mt-2">
          <div className="text-xs text-gray-600 text-center mb-1">
            {completedCount}/{totalCount}
          </div>
          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-blue-500 to-green-500"
              initial={{ width: 0 }}
              animate={{ width: `${(completedCount / totalCount) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
      </div>

      {/* 拼图块网格 - 单列布局 */}
      <div className="p-2 flex-1 overflow-y-auto">
        {uncompletedProvinces.length === 0 ? (
          <motion.div
            className="flex flex-col items-center justify-center h-full text-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-3xl mb-2">🎉</div>
            <h3 className="text-sm font-bold text-green-600 mb-1">
              全部完成！
            </h3>
            <p className="text-xs text-gray-600">
              所有省份已正确放置
            </p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 gap-2">
            {uncompletedProvinces.map((province, index) => (
              <motion.div
                key={province.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.3, 
                  delay: index * 0.05,
                  type: 'spring',
                  stiffness: 300,
                  damping: 30
                }}
                className="flex justify-center"
              >
                <DraggableProvinceBlock
                  province={province}
                  showName={showProvinceNames}
                  showAbbreviation={showAbbreviations && globalShowAbbreviations}
                  isHinted={hintedProvinceId === province.id}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                />
              </motion.div>
            ))}
          </div>
        )}

        {/* 拖拽提示 */}
        {uncompletedProvinces.length > 0 && (
          <motion.div
            className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-start space-x-2">
              <span className="text-blue-500 mt-0.5">💡</span>
              <div className="text-sm text-blue-700">
                <div className="font-medium mb-1">操作提示：</div>
                <ul className="space-y-1 text-xs">
                  <li>• 拖拽省份拼图块到地图对应位置</li>
                  <li>• 接近正确位置时会有颜色提示</li>
                  <li>• 使用提示功能可以高亮显示位置</li>
                  <li>• 完成的省份会自动从这里移除</li>
                </ul>
              </div>
            </div>
          </motion.div>
        )}

        {/* 优化的拖拽状态提示 */}
        {draggedProvince && (
          <motion.div
            className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-3 py-1.5 rounded-full shadow-lg z-50 text-sm font-medium"
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center space-x-2">
              <span className="text-base">🎯</span>
              <span>
                拖拽 {draggedProvince.name} 到地图
              </span>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};
